import { useEffect, Suspense, lazy, startTransition } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import { getUserSession, checkEmailVerification } from '../services/api';
import { logout, setToken } from '../redux/reducers/authReducer';
import { clearUser, setUser } from '../redux/reducers/userReducer';
import ErrorBoundary from '../components/ErrorBoundary';
import AuthErrorBoundary from '../components/AuthErrorBoundary';
import LoadingSpinner from '../components/LoadingSpinner';
import { safeLog } from '../utils/security';

// Lazy load components for better performance
const DashboardLayout = lazy(() => import('../components/Dashboard/DashboardLayout'));
const Overview = lazy(() => import('../pages/Dashboard/Overview'));
const Claims = lazy(() => import('../pages/Dashboard/Claims'));
const CallLogs = lazy(() => import('../pages/Dashboard/CallLogs'));
const Patients = lazy(() => import('../pages/Dashboard/Patients'));
const Payers = lazy(() => import('../pages/Dashboard/Payers'));
const Onboarding = lazy(() => import('../pages/Onboarding'));
const LogIn = lazy(() => import('../pages/Auth/LogIn'));
const ResetPassword = lazy(() => import('../pages/Auth/ResetPassword'));
const SignUp = lazy(() => import('../pages/Auth/SignUp'));
const Verification = lazy(() => import('../pages/Auth/Verification'));
const Registration = lazy(() => import('../pages/Auth/Registration'));


// Helper function to check if user needs verification
// Only returns true if isVerified is explicitly false, not undefined
const needsVerification = (isAuthenticated: boolean, isVerified: boolean | undefined): boolean => {
  return isAuthenticated && isVerified === false;
};

// Component to handle auth validation on app load
const AuthValidator: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, token } = useSelector((state: RootState) => state.auth);
  const userData = useSelector((state: RootState) => state.user.userData);

  // List of auth pages where users should be allowed even if not verified
  const authPages = ['/auth/login', '/auth/signup', '/auth/verification', '/auth/registration', '/auth/reset-password'];
  const isOnAuthPage = authPages.some(page => location.pathname.startsWith(page));

  useEffect(() => {
    const validateSession = async () => {
      const storedToken = sessionStorage.getItem('authToken');
      
      // If we have a token in storage but not in Redux, restore the auth state
      if (storedToken && !isAuthenticated) {
        try {
          const response = await getUserSession();
          if (response.success) {
            // Session is valid, restore auth state
            dispatch(setToken(storedToken));
            // Try to get user data if we don't have it
            try {
              const verificationResponse = await checkEmailVerification();
              const isVerified = verificationResponse.data.email_verified;
              dispatch(setUser({
                user_id: verificationResponse.data.uid,
                email: verificationResponse.data.email,
                first_name: '', // These might not be available from verification endpoint
                last_name: '',
                uid: verificationResponse.data.uid,
                is_verified: isVerified,
              }));
              
              // If user is not verified and NOT on an auth page, redirect to verification page
              if (!isVerified && !isOnAuthPage) {
                startTransition(() => {
                  navigate('/auth/verification', { replace: true });
                });
              }
            } catch (userErr) {
              // If we can't get user data, that's okay, we'll get it on next login
              safeLog.warn('Could not fetch user data during session restore:', userErr);
            }
          } else {
            // Session is invalid, clear everything
            startTransition(() => {
              sessionStorage.removeItem('authToken');
              dispatch(logout());
              dispatch(clearUser());
            });
          }
        } catch (error) {
          // API call failed, clear everything
          startTransition(() => {
            sessionStorage.removeItem('authToken');
            dispatch(logout());
            dispatch(clearUser());
          });
        }
      } else if (!storedToken && isAuthenticated) {
        // We have Redux state but no token in storage, clear Redux
        dispatch(logout());
        dispatch(clearUser());
      } else if (isAuthenticated && userData && needsVerification(isAuthenticated, userData.is_verified)) {
        // If user is authenticated but not verified, redirect to verification
        // BUT only if they're not already on an auth page (allow them to edit email, etc.)
        if (!isOnAuthPage) {
          startTransition(() => {
            navigate('/auth/verification', { replace: true });
          });
        }
      }
    };

    // Only run once on mount
    validateSession();
  }, [dispatch, isAuthenticated, token, navigate, userData, isOnAuthPage]);

  return null;
};

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const userData = useSelector((state: RootState) => state.user.userData);
  const isVerified = userData?.is_verified;

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  // If authenticated but not verified, redirect to verification page
  if (needsVerification(isAuthenticated, isVerified)) {
    return <Navigate to="/auth/verification" replace />;
  }

  // User is authenticated and verified, allow access
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        <AuthValidator />
        <Suspense fallback={<LoadingSpinner message="Loading application..." />}>
          <Routes>
            {/* Make Onboarding the default route for the app */}
            <Route path="/" element={<Onboarding />} />
            <Route path="/auth/login" element={
              <AuthErrorBoundary>
                <LogIn />
              </AuthErrorBoundary>
            } />
            <Route path="/auth/signup" element={
              <AuthErrorBoundary>
                <SignUp />
              </AuthErrorBoundary>
            } />
            <Route path="/auth/verification" element={
              <AuthErrorBoundary>
                <Verification />
              </AuthErrorBoundary>
            } />
            <Route path="/auth/registration" element={
              <AuthErrorBoundary>
                <Registration />
              </AuthErrorBoundary>
            } />
            <Route path="/auth/reset-password" element={
              <AuthErrorBoundary>
                <ResetPassword />
              </AuthErrorBoundary>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Overview />} />
              <Route path="overview" element={<Overview />} />
              <Route path="claim-history" element={<Claims />} />
              <Route path="call-logs" element={<CallLogs />} />
              <Route path="patients" element={<Patients />} />
              <Route path="payers" element={<Payers />} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
};

export default AppRoutes;